import type { OrderOptionDto } from '../../dto/order';
import { getQuantitySetValue, resolveStoredQuantitySet } from '../../utils/commercialQuantity';
import { getOrderProductDisplayDimensions } from '../orders/orderProductDisplay';
import { isClosedOrderStatus } from './closedOrders';
import type { SupplierCargoLine } from './supplierTruckCargo';

/**
 * „Dla kogo jest ten towar?” (2026-10-09, blind agent runs and prod trucks):
 * on a truck from the factory each ZD line on the loading side names its
 * recipients — customer order lines (ZK) of the same catalog variant — or
 * goes to our warehouse. Choosing a recipient puts the ZK on the truck and
 * pairs its line with this ZD line (the same pair as „z pozycji ZD”).
 * A ZD generated from a ZK (`sourceOrderId`) carries that customer's goods:
 * only that ZK is suggested; other ZK of the variant wait under „Dodaj innego
 * klienta” (Tomek 2026-10-21: every Mobilbox ZK showed up). A source ZK that is
 * closed, not listed or without this product (a line added to the ZD by hand,
 * review PR #415) leaves the goods free: every open ZK is suggested again.
 * Display/selection rules only; the money rule stays on the server.
 */

export interface RecipientCandidate {
  orderId: string;
  orderProductId: string;
  orderLabel: string;
  customerName: string;
  productName: string;
  /** Quantity of the ZK line in the ZD line's unit; null = cannot be expressed. */
  quantity: number | null;
  /** The ZK line's quantity equals what the ZD line orders. */
  quantityMatches: boolean;
}

export interface LineRecipients {
  line: SupplierCargoLine;
  /** ZK lines on this truck paired with this ZD line. */
  assigned: RecipientCandidate[];
  /** Open ZK lines of the same variant not on this truck yet (best match first). */
  suggestions: RecipientCandidate[];
  /** Line from a ZK-generated ZD: open ZK lines of the variant outside its source ZK. */
  others: RecipientCandidate[];
  /** The ZD line's ordered amount in its own unit. */
  orderedQuantity: number | null;
}

function lineAmount(source: Record<string, unknown>, unit: string | null): number | null {
  if (!unit) return null;
  return getQuantitySetValue(resolveStoredQuantitySet(source), unit as 'pcs' | 'm2' | 'm3');
}

function toCandidate(order: OrderOptionDto, product: OrderOptionDto['products'][number], line: SupplierCargoLine): RecipientCandidate {
  const quantity = lineAmount(product as unknown as Record<string, unknown>, line.unit);
  const ordered = line.unit ? getQuantitySetValue(line.ordered, line.unit) : null;
  return {
    orderId: order.id,
    orderProductId: product.id,
    orderLabel: order.displayOrderNumber || order.orderNumber,
    customerName: order.customer?.name || order.customerName || '',
    productName: product.name,
    quantity,
    quantityMatches: quantity !== null && ordered !== null && Math.abs(quantity - ordered) < 0.0005,
  };
}

/**
 * @param pairedLineByRowKey cargo row key (`orderId:orderProductId`) → paired ZD line id
 */
export function buildLineRecipients(
  lines: SupplierCargoLine[],
  orders: OrderOptionDto[],
  selectedOrderIds: string[],
  pairedLineByRowKey: ReadonlyMap<string, string | null>,
  rowKeyOf: (orderId: string, orderProductId: string) => string,
): LineRecipients[] {
  const selected = new Set(selectedOrderIds);
  const openOrderById = new Map(orders
    .filter((order) => selected.has(order.id) || !isClosedOrderStatus(order.status))
    .map((order) => [order.id, order]));
  const ordersVariant = (order: OrderOptionDto | undefined, variantId: string) => Boolean(order?.productsLoaded
    && order.products.some((product) => product.productVariantId === variantId && !(product as { removedAt?: unknown }).removedAt));
  return lines.map((line) => {
    const sourceOrderId = line.sourceOrderId && line.productVariantId
      && ordersVariant(openOrderById.get(line.sourceOrderId), line.productVariantId) ? line.sourceOrderId : null;
    const assigned: RecipientCandidate[] = [];
    const suggestions: RecipientCandidate[] = [];
    const others: RecipientCandidate[] = [];
    if (line.productVariantId) {
      orders.forEach((order) => {
        const onTruck = selected.has(order.id);
        if (!onTruck && isClosedOrderStatus(order.status)) return;
        (order.productsLoaded ? order.products : []).forEach((product) => {
          if (product.productVariantId !== line.productVariantId) return;
          if ((product as { removedAt?: unknown }).removedAt) return;
          const candidate = toCandidate(order, product, line);
          if (onTruck) {
            if (pairedLineByRowKey.get(rowKeyOf(order.id, product.id)) === line.itemId) assigned.push(candidate);
          } else if (sourceOrderId && order.id !== sourceOrderId) {
            others.push(candidate);
          } else {
            suggestions.push(candidate);
          }
        });
      });
    }
    const bestFirst = (a: RecipientCandidate, b: RecipientCandidate) => Number(b.quantityMatches) - Number(a.quantityMatches)
      || a.orderLabel.localeCompare(b.orderLabel, 'pl');
    suggestions.sort(bestFirst);
    others.sort(bestFirst);
    return {
      line,
      assigned,
      suggestions,
      others,
      orderedQuantity: line.unit ? getQuantitySetValue(line.ordered, line.unit) : null,
    };
  });
}

/** ZK numbers whose goods match a ZD line on the truck but are not on it. */
export function unassignedMatches(recipients: LineRecipients[]): RecipientCandidate[] {
  const seen = new Set<string>();
  return recipients
    .filter((entry) => entry.assigned.length === 0)
    .flatMap((entry) => entry.suggestions.filter((candidate) => candidate.quantityMatches))
    .filter((candidate) => (seen.has(candidate.orderId) ? false : (seen.add(candidate.orderId), true)));
}

/**
 * Owner 2026-10-10 (variant B „Przeciągnij na klienta”): the drop board of a
 * factory truck — goods (ZD lines with what is still unassigned) on the left,
 * customer stops (ZK on the truck + suggested ZK) on the right. Dropping a
 * goods line on a stop pairs it with that ZK's line of the same variant.
 */
export interface BoardGoods {
  line: SupplierCargoLine;
  /** What this truck carries of the line (its unit). */
  carried: number | null;
  /** Σ ZK cargo paired with it (its unit). */
  assigned: number;
}

export interface BoardStopLine {
  key: string;
  orderProductId: string;
  productName: string;
  /** „gr. 12 x szer. 1250 x dł. 3000 mm” of the ZK line; null when unknown (display only). */
  dimensions?: string | null;
  productVariantId: string | null;
  unit: string | null;
  /** What the customer still needs moved (order remaining). */
  need: number | null;
  /** On this truck (cargo row quantity); null when the ZK is not on the truck. */
  onTruck: number | null;
  pairedLineId: string | null;
}

export interface BoardStop {
  orderId: string;
  orderLabel: string;
  customerName: string;
  place: string | null;
  onTruck: boolean;
  lines: BoardStopLine[];
}

export interface BoardRow {
  key: string;
  orderId: string;
  orderProductId?: string;
  productName: string;
  /** The form row's formatted dimensions (`CargoTableRow.productDimensions`). */
  productDimensions?: string | null;
  productVariantId?: string | null;
  unit?: string | null;
  quantity?: number | null;
  availableQuantity?: number;
}

function amountInLineUnit(row: BoardRow, line: SupplierCargoLine): number {
  if (!line.unit) return 0;
  if (!row.unit || row.unit === line.unit) return Number(row.quantity) || 0;
  return getQuantitySetValue(resolveStoredQuantitySet(row as unknown as Record<string, unknown>), line.unit as 'pcs' | 'm2' | 'm3') ?? 0;
}

export function buildDropBoard(
  lines: SupplierCargoLine[],
  carriedByLine: ReadonlyMap<string, number | null>,
  orders: OrderOptionDto[],
  selectedOrderIds: string[],
  cargoRows: BoardRow[],
  pairedLineByRowKey: ReadonlyMap<string, string | null>,
  extraOrderIds: string[] = [],
  suggestionLimit = 4,
): { goods: BoardGoods[]; stops: BoardStop[]; addable: BoardStop[] } {
  const variants = new Set(lines.map((line) => line.productVariantId).filter(Boolean) as string[]);
  const goods = lines.map((line) => ({
    line,
    carried: carriedByLine.get(line.itemId) ?? null,
    assigned: cargoRows
      .filter((row) => pairedLineByRowKey.get(row.key) === line.itemId)
      .reduce((sum, row) => sum + amountInLineUnit(row, line), 0),
  }));
  const orderById = new Map(orders.map((order) => [order.id, order]));
  // A ZK line with nothing on this truck, paired with a ZD line this truck does
  // not carry („Nic” in „Co jedzie”), is not part of this stop (film 04/12/2025).
  const offTruck = (row: BoardRow) => {
    const lineId = pairedLineByRowKey.get(row.key);
    if (!lineId || (Number(row.quantity) || 0) > 0.0005) return false;
    const carried = carriedByLine.get(lineId);
    return carried !== undefined && carried !== null && carried <= 0.0005;
  };
  const stopOf = (order: OrderOptionDto, onTruck: boolean): BoardStop => ({
    orderId: order.id,
    orderLabel: order.displayOrderNumber || order.orderNumber,
    customerName: order.customer?.name || order.customerName || '',
    place: order.deliveryLocation || null,
    onTruck,
    lines: onTruck
      ? cargoRows.filter((row) => row.orderId === order.id && row.orderProductId && !offTruck(row)).map((row) => ({
        key: row.key,
        orderProductId: row.orderProductId as string,
        productName: row.productName,
        dimensions: row.productDimensions ?? null,
        productVariantId: row.productVariantId ?? null,
        unit: row.unit ?? null,
        need: row.availableQuantity ?? null,
        onTruck: Number(row.quantity) || 0,
        pairedLineId: pairedLineByRowKey.get(row.key) ?? null,
      }))
      : (order.productsLoaded ? order.products : [])
        .filter((product) => product.productVariantId && variants.has(product.productVariantId) && !(product as { removedAt?: unknown }).removedAt)
        .map((product) => ({
          key: `${order.id}:${product.id}`,
          orderProductId: product.id,
          productName: product.name,
          dimensions: getOrderProductDisplayDimensions(product),
          productVariantId: product.productVariantId ?? null,
          unit: product.unit ?? null,
          need: Number(product.quantity) || null,
          onTruck: null,
          pairedLineId: null,
        })),
  });
  const onTruck = selectedOrderIds.map((id) => orderById.get(id)).filter((order): order is OrderOptionDto => Boolean(order))
    .map((order) => stopOf(order, true));
  const recipients = buildLineRecipients(lines, orders, selectedOrderIds, pairedLineByRowKey, (orderId, productId) => `${orderId}:${productId}`);
  const candidateIds: string[] = [];
  const ranked = recipients.flatMap((entry) => entry.suggestions)
    .sort((a, b) => Number(b.quantityMatches) - Number(a.quantityMatches));
  ranked.forEach((candidate) => { if (!candidateIds.includes(candidate.orderId)) candidateIds.push(candidate.orderId); });
  // Owner 2026-10-12 (edge run: one ZD line on five trucks): a ZK with the
  // same goods is suggested even when its quantity is not the ZD line's —
  // exact matches first, then the others, up to the limit.
  // ZK outside the source ZK of a generated ZD only under „Dodaj innego klienta”.
  const otherIds: string[] = [];
  recipients.flatMap((entry) => entry.others)
    .sort((a, b) => Number(b.quantityMatches) - Number(a.quantityMatches))
    .forEach((candidate) => {
      if (!candidateIds.includes(candidate.orderId) && !otherIds.includes(candidate.orderId)) otherIds.push(candidate.orderId);
    });
  const addableIds = [...candidateIds, ...otherIds];
  const shownIds = candidateIds.slice(0, suggestionLimit)
    .concat(extraOrderIds.filter((id) => addableIds.includes(id)));
  const stops = [...onTruck, ...Array.from(new Set(shownIds)).map((id) => stopOf(orderById.get(id)!, false))];
  const addable = addableIds.filter((id) => !shownIds.includes(id)).map((id) => stopOf(orderById.get(id)!, false));
  return { goods, stops, addable };
}

const DROP_TOLERANCE = 0.0005;

/**
 * FA.3 (owner 2026-10-12, film 03/12/2025): goods dropped on a customer not on
 * this truck yet go to the first ZK line of that product other trucks do not
 * carry already. `remainingByProductId` = what each ZK line still needs after
 * the other trucks (null = unknown: the board's pick and need stay).
 */
export function pickDropTarget(
  stop: BoardStop,
  droppedOn: BoardStopLine,
  remainingByProductId: Readonly<Record<string, number>> | null,
): { target: BoardStopLine; need: number | null } {
  if (!remainingByProductId) return { target: droppedOn, need: droppedOn.need ?? null };
  const remaining = (line: BoardStopLine) => remainingByProductId[line.orderProductId] ?? line.need ?? 0;
  const open = stop.lines.find((line) => line.productVariantId === droppedOn.productVariantId && remaining(line) > DROP_TOLERANCE);
  const target = open ?? droppedOn;
  return { target, need: remaining(target) };
}

export interface DropPlanInput {
  /** The ZD line dropped. */
  lineId: string;
  /** Current board goods (what the truck carries and what is assigned). */
  goods: BoardGoods[];
  /** The stop as the board shows it now (a fresh ZK: already on the truck). */
  stop: BoardStop;
  /** Cargo row key of the ZK line that takes the goods. */
  targetKey: string;
  /** What the ZK line still needs (null = unknown). */
  need: number | null;
  /**
   * The ZK was just put on the truck by this drop: its other lines start at 0
   * unless this truck also carries their goods (another ZD line of that
   * product with something on the truck) — those wait for their own drop.
   */
  fresh: boolean;
  /** All lines of the ZK (variant per line), for the zero start of a fresh ZK. */
  orderProducts?: Array<{ id: string; productVariantId?: string | null }>;
  /**
   * ZD lines whose quantity on this truck is only the Σ of their customers
   * (untyped, `deriveSupplierCargoEntries`). Their „carried” counts this ZK's
   * own seeded rows, so whether the truck brings their goods is decided
   * without them (live run 2026-10-12: a line set to „Nic” must not ride
   * because the fresh ZK's default quantity paired with it). When nothing is
   * left without them, `baseCarried` decides: what the line carries with no
   * customer sums at all (the rest → yes, „Nic” → no).
   */
  derivedLineIds?: ReadonlySet<string>;
  baseCarried?: ReadonlyMap<string, number | null>;
}

/**
 * Quantities one drop writes into the ZK cargo rows (row key → quantity):
 * the target takes what this truck still brings of the ZD line (its unit),
 * capped by what the ZK line needs; what this ZK already took of the line and
 * gives back with this drop counts as free. Pure: the form writes the result
 * and marks the rows touched (SharedTransportForm, `dropQuantities`).
 */
export function planDrop(input: DropPlanInput): Record<string, number> {
  const { lineId, goods, stop, targetKey, need, fresh } = input;
  const goodsItem = goods.find((item) => item.line.itemId === lineId);
  const quantities: Record<string, number> = {};
  if (fresh) {
    const ownOf = (itemId: string) => stop.lines
      .filter((line) => line.pairedLineId === itemId)
      .reduce((sum, line) => sum + (Number(line.onTruck) || 0), 0);
    const carriedWithoutStop = (item: BoardGoods) => {
      if (item.carried === null || !input.derivedLineIds?.has(item.line.itemId)) return item.carried;
      const others = item.carried - ownOf(item.line.itemId);
      if (others > DROP_TOLERANCE) return others;
      const base = input.baseCarried?.get(item.line.itemId);
      return base === undefined ? others : base;
    };
    const carriedVariants = new Set(goods
      .filter((item) => {
        if (item.line.itemId === lineId) return false;
        const carried = carriedWithoutStop(item);
        return carried === null || carried > DROP_TOLERANCE;
      })
      .map((item) => item.line.productVariantId)
      .filter(Boolean));
    (input.orderProducts ?? []).forEach((product) => {
      const key = `${stop.orderId}:${product.id}`;
      if (key === targetKey) return;
      if (product.productVariantId && carriedVariants.has(product.productVariantId)) return;
      quantities[key] = 0;
    });
  }
  const target = stop.lines.find((line) => line.key === targetKey);
  const own = stop.lines
    .filter((line) => line.pairedLineId === lineId && (line.key === targetKey || quantities[line.key] === 0))
    .reduce((sum, line) => sum + (Number(line.onTruck) || 0), 0);
  const sameUnit = !target?.unit || !goodsItem?.line.unit || target.unit === goodsItem.line.unit;
  const left = goodsItem && goodsItem.carried !== null && sameUnit
    ? Math.max(goodsItem.carried - goodsItem.assigned + own, 0)
    : null;
  const quantity = left === null ? need : need === null ? left : Math.min(left, need);
  if (quantity !== null) quantities[targetKey] = quantity;
  return quantities;
}
